import { useEffect, useState, useRef } from "react";
import {
  IBusinessHourData,
  IWorkingHours,
  IClosure,
} from "@/api/businessLinesApi";
import { WorkingHoursTable } from "./WorkingHoursTable";
import DefaultClosures from "./defaultclosures";
import { toast } from "react-toastify";
import { useBusiness } from "../business_context";
import TimezoneSelector from "@/components/TimezoneSelector";

export default function WorkingHoursTab() {
  const {
    selectedLineId,
    getCurrentWorkingHoursData,
    defaultClosures,
    isDataLoading,
    isDefaultClosuresLoading,
    updateWorkingHoursData,
    resetWorkingHoursData,
    saveWorkingHoursData,
    hasWorkingHoursChanges,
    changedWorkingHoursData,
    isLineActive,
  } = useBusiness();

  const [workingHours, setWorkingHours] = useState<IWorkingHours>({
    mo: null,
    tu: null,
    we: null,
    th: null,
    fr: null,
    sa: null,
    su: null,
  });

  const [closures, setClosures] = useState<IClosure[]>([]);
  const [businessHourData, setBusinessHourData] =
    useState<IBusinessHourData | null>(null);

  // Add state for selected timezone
  const [selectedTimezone, setSelectedTimezone] = useState<string>(
    "America/Los_Angeles"
  );

  // Add ref to track if we're saving to prevent state reset
  const isSavingRef = useRef(false);

  // When businessHourData changes, update selectedTimezone from it if available
  useEffect(() => {
    if (businessHourData && businessHourData.timezone) {
      setSelectedTimezone(businessHourData.timezone);
    }
  }, [businessHourData]);

  // Update local state when context data changes
  useEffect(() => {
    const currentWorkingHoursData = getCurrentWorkingHoursData();
    if (currentWorkingHoursData && !isSavingRef.current) {
      setBusinessHourData(currentWorkingHoursData);
      setClosures(currentWorkingHoursData.closures || []);
      setWorkingHours(currentWorkingHoursData.working_hours);
    }
  }, [getCurrentWorkingHoursData]);

  // Reset local state when changed data is cleared (e.g., from "Discard changes and switch")
  useEffect(() => {
    if (changedWorkingHoursData === null && !isSavingRef.current) {
      const currentWorkingHoursData = getCurrentWorkingHoursData();
      if (currentWorkingHoursData) {
        setBusinessHourData(currentWorkingHoursData);
        setClosures(currentWorkingHoursData.closures || []);
        setWorkingHours(currentWorkingHoursData.working_hours);
      }
    }
  }, [changedWorkingHoursData, getCurrentWorkingHoursData]);

  // Handle timezone change
  const handleTimezoneChange = (newTimezone: string) => {
    setSelectedTimezone(newTimezone);

    // Update local businessHourData state immediately
    if (businessHourData) {
      const updatedBusinessHourData = {
        ...businessHourData,
        timezone: newTimezone,
      };
      setBusinessHourData(updatedBusinessHourData);

      // Update context
      updateWorkingHoursData(updatedBusinessHourData);
    }
  };

  // Show loading state
  if (isDataLoading || isDefaultClosuresLoading) {
    return (
      <div className="p-4">
        <div className="animate-pulse space-y-4">
          <div className="h-8 bg-gray-200 rounded w-1/3"></div>
          <div className="h-32 bg-gray-200 rounded"></div>
          <div className="h-24 bg-gray-200 rounded"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 space-y-8">
      {!isLineActive && (
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-4">
          <div className="flex items-center gap-2">
            <div className="w-2 h-2 bg-yellow-500 rounded-full"></div>
            <p className="text-yellow-800 font-medium">
              Read-only mode: This line is not active. You can only view working
              hours data.
            </p>
          </div>
        </div>
      )}

      <TimezoneSelector
        value={selectedTimezone}
        onChange={handleTimezoneChange}
        isReadOnly={!isLineActive}
      />
      <WorkingHoursTable
        workingHours={workingHours}
        setWorkingHours={
          isLineActive
            ? (newWorkingHours) => {
                const updatedWorkingHours =
                  typeof newWorkingHours === "function"
                    ? newWorkingHours(workingHours)
                    : newWorkingHours;

                setWorkingHours(updatedWorkingHours);

                // Update local businessHourData state immediately
                if (businessHourData) {
                  const updatedBusinessHourData = {
                    ...businessHourData,
                    working_hours: updatedWorkingHours,
                  };
                  setBusinessHourData(updatedBusinessHourData);

                  // Update context
                  updateWorkingHoursData(updatedBusinessHourData);
                }
              }
            : () => {}
        }
        isReadOnly={!isLineActive}
      />

      {/* Closures section */}
      <div className="flex flex-col justify-start items-start gap-1">
        <div className="text-zinc-700 text-sm font-medium">Closures</div>
        <div className="text-gray-500 text-xs">
          Keep your callers updated with the days when your business is closed
        </div>
      </div>

      {/* default closures */}
      <div className="flex items-start gap-8">
        <DefaultClosures
          closures={closures}
          setClosures={
            isLineActive
              ? (newClosures) => {
                  const updatedClosures =
                    typeof newClosures === "function"
                      ? newClosures(closures)
                      : newClosures;

                  setClosures(updatedClosures);

                  // Update local businessHourData state immediately
                  if (businessHourData) {
                    const updatedBusinessHourData = {
                      ...businessHourData,
                      closures: updatedClosures,
                    };
                    setBusinessHourData(updatedBusinessHourData);

                    // Update context
                    updateWorkingHoursData(updatedBusinessHourData);
                  }
                }
              : () => {}
          }
          defaultClosures={
            defaultClosures?.map((closure) => ({
              ...closure,
              id: closure.id.toString(),
              business_line_id: null,
            })) || []
          }
          businessLineId={selectedLineId}
          isReadOnly={!isLineActive}
        />
      </div>

      {hasWorkingHoursChanges() && isLineActive && (
        <div className="flex justify-end mt-4 gap-3">
          <button
            className="w-28 h-9 px-2 py-1 bg-gray-100 rounded-lg flex justify-center items-center gap-1 text-zinc-700 text-sm font-normal leading-tight hover:bg-gray-200"
            onClick={() => resetWorkingHoursData()}
          >
            Cancel
          </button>

          <button
            className="w-28 h-9 px-2 py-1 bg-emerald-500 rounded-lg flex justify-center items-center gap-1 text-white text-sm font-normal leading-tight hover:bg-emerald-600"
            onClick={async () => {
              try {
                await saveWorkingHoursData();
                toast.success("Business hours updated successfully");
              } catch (error) {
                console.error("Error updating business hours:", error);
                toast.error("Error updating business hours");
              }
            }}
          >
            Save Changes
          </button>
        </div>
      )}
    </div>
  );
}
